/**
 * useQuickPractice Hook
 *
 * Manages the lifecycle of quick practice generation:
 * - API call to generate/retrieve cached micro-lesson
 * - In-memory cache by messageId + errorType
 * - Loading, error, and data states
 */

import { useState, useCallback, useRef } from 'react';
import { api } from '../services/api';
import { HTTP_AI_TIMEOUT_MS } from '../config/timeouts';
import { errorMessage } from '../generated/api/endpoint-errors';
import { useUserPreferencesStore } from '../store/userPreferencesStore';
import type { QuickPracticeContent } from '../types/lesson';

export interface QuickPracticeParams {
  messageId: string;
  errorType: string;
  original: string;
  correction: string;
  explanation: string;
}

interface QuickPracticeState {
  loading: boolean;
  error: string | null;
  data: { id: string; content: QuickPracticeContent } | null;
}

function cacheKey(messageId: string, errorType: string): string {
  return `${messageId}:${errorType}`;
}

export function useQuickPractice() {
  const [state, setState] = useState<QuickPracticeState>({
    loading: false,
    error: null,
    data: null,
  });

  // In-memory cache — survives re-renders but not unmounts
  const cacheRef = useRef<Map<string, { id: string; content: QuickPracticeContent }>>(new Map());

  const generate = useCallback(async (params: QuickPracticeParams) => {
    const key = cacheKey(params.messageId, params.errorType);

    // Check in-memory cache first
    const cached = cacheRef.current.get(key);
    if (cached) {
      setState({ loading: false, error: null, data: cached });
      return cached;
    }

    setState({ loading: true, error: null, data: null });

    const courseId = useUserPreferencesStore.getState().activeCourseId ?? '';
    // LLM generation — long-op budget, not the fail-fast default.
    const result = await api.generateQuickPractice({ ...params, courseId }, { timeout: HTTP_AI_TIMEOUT_MS });

    if (!result.ok) {
      setState({ loading: false, error: errorMessage(result), data: null });
      return null;
    }

    // Cache the result — generated type uses Record<string, any> for AI content
    const typed = result.data as { id: string; content: QuickPracticeContent };
    cacheRef.current.set(key, typed);

    setState({ loading: false, error: null, data: typed });
    return typed;
  }, []);

  const clear = useCallback(() => {
    setState({ loading: false, error: null, data: null });
  }, []);

  return {
    ...state,
    generate,
    clear,
  };
}
